import { useEffect, useRef, useCallback } from 'react'
import { useDStore } from '@/stores/useDStore'
import { DChatMessage } from './DChatMessage'
import { DChatInput } from './DChatInput'
import { DThinkingIndicator } from './DThinkingIndicator'
import { DThinkingBlock } from './DThinkingBlock'
import { streamDGatekeeper } from '@/lib/api'
import type { FinalDiff } from '@/lib/api'

export function DChat() {
  const {
    sessionId,
    documentContent,
    foolscapSnapshot,
    workflowStatus,
    setWorkflowStatus,
    setError,
    // Turns (conversation history)
    gatekeeperTurns,
    addGatekeeperUserTurn,
    startGatekeeperStreaming,
    finishGatekeeperStreaming,
    // Zinsser state
    zinsserStreamingThinking,
    zinsserIsThinking,
    zinsserIsProcessing,
    zinsserHasThinking,
    // Zinsser streaming actions
    startZinsserProcessing,
    startZinsserThinking,
    updateZinsserThinking,
    finishZinsserThinking,
    finishZinsserProcessing,
    // StrengthenVerbs state (micro editing - Pass 1)
    strengthenVerbsStreamingThinking,
    strengthenVerbsIsThinking,
    strengthenVerbsIsProcessing,
    strengthenVerbsHasThinking,
    // StrengthenVerbs streaming actions
    startStrengthenVerbsProcessing,
    startStrengthenVerbsThinking,
    updateStrengthenVerbsThinking,
    finishStrengthenVerbsThinking,
    finishStrengthenVerbsProcessing,
    // CheckLogic state (micro editing - Pass 2)
    checkLogicStreamingThinking,
    checkLogicIsThinking,
    checkLogicIsProcessing,
    checkLogicHasThinking,
    // CheckLogic streaming actions
    startCheckLogicProcessing,
    startCheckLogicThinking,
    updateCheckLogicThinking,
    finishCheckLogicThinking,
    finishCheckLogicProcessing,
    // RhythmSound state (micro editing - Pass 3)
    rhythmSoundStreamingThinking,
    rhythmSoundIsThinking,
    rhythmSoundIsProcessing,
    rhythmSoundHasThinking,
    // RhythmSound streaming actions
    startRhythmSoundProcessing,
    startRhythmSoundThinking,
    updateRhythmSoundThinking,
    finishRhythmSoundThinking,
    finishRhythmSoundProcessing,
    // Responder state (coaching response after editing)
    responderStreamingThinking,
    responderIsThinking,
    responderIsProcessing,
    // Responder streaming actions
    startResponderProcessing,
    startResponderThinking,
    updateResponderThinking,
    finishResponderThinking,
    finishResponderProcessing,
    // Real-time canvas editing
    applyCanvasEdit,
    applyCanvasInsert,
    // Final diff state (setFinalDiff stores it, then finishResponderProcessing attaches it to the turn)
    setFinalDiff,
    // Initial trigger flag (set when session is created from Foolscap picker)
    pendingInitialTrigger,
    clearPendingInitialTrigger,
  } = useDStore()

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Stream drafter workflow with a message
  const triggerDrafter = useCallback(async (content: string) => {
    if (!sessionId) {
      setError('No session ID available')
      return
    }

    if (content.trim()) {
      addGatekeeperUserTurn(content)
    }
    setWorkflowStatus('submitting')

    try {
      startGatekeeperStreaming()

      await streamDGatekeeper(
        {
          content,
          session_id: sessionId,
          new_doc: documentContent || undefined,
          foolscap_snapshot: foolscapSnapshot || undefined,
        },
        {
          // D workflow has no Gatekeeper - these are no-ops for API compatibility
          onThinkingStart: () => {},
          onThinkingDelta: () => {},
          onThinkingComplete: () => {},
          onGatekeeperDone: async (output) => {
            finishGatekeeperStreaming(output)
          },
          // Zinsser (Drafter) start
          onZinsserStart: () => {
            startZinsserProcessing()
          },
          // Zinsser (Drafter) thinking
          onZinsserThinkingStart: () => {
            startZinsserProcessing()
            startZinsserThinking()
          },
          onZinsserThinkingDelta: (delta) => updateZinsserThinking(delta),
          onZinsserThinkingComplete: () => finishZinsserThinking(),
          // Zinsser edits - apply to canvas in real-time
          onZinsserEdit: (oldStr, newStr) => {
            console.log('[DChat] Zinsser edit:', oldStr.slice(0, 50), '→', newStr.slice(0, 50))
            applyCanvasEdit(oldStr, newStr)
          },
          // Zinsser inserts - for creation mode when building from blank canvas
          onZinsserInsert: (insertLine, insertText) => {
            console.log('[DChat] Zinsser insert at line', insertLine, ':', insertText.slice(0, 50))
            applyCanvasInsert(insertLine, insertText)
          },
          // Zinsser done
          onZinsserDone: (output) => {
            console.log('[DChat] Zinsser done - full output:', output)
            finishZinsserProcessing(output)
          },
          // StrengthenVerbs (micro editing - Pass 1)
          onStrengthenVerbsStart: () => {
            startStrengthenVerbsProcessing()
          },
          onStrengthenVerbsThinkingStart: () => {
            startStrengthenVerbsProcessing()
            startStrengthenVerbsThinking()
          },
          onStrengthenVerbsThinkingDelta: (delta) => updateStrengthenVerbsThinking(delta),
          onStrengthenVerbsThinkingComplete: () => finishStrengthenVerbsThinking(),
          // StrengthenVerbs edits - apply to canvas in real-time
          onStrengthenVerbsEdit: (oldStr, newStr) => {
            console.log('[DChat] StrengthenVerbs edit:', oldStr.slice(0, 50), '→', newStr.slice(0, 50))
            applyCanvasEdit(oldStr, newStr)
          },
          // StrengthenVerbs done
          onStrengthenVerbsDone: (output) => {
            console.log('[DChat] StrengthenVerbs done - full output:', output)
            finishStrengthenVerbsProcessing(output)
          },
          // CheckLogic (micro editing - Pass 2)
          onCheckLogicStart: () => {
            startCheckLogicProcessing()
          },
          onCheckLogicThinkingStart: () => {
            startCheckLogicProcessing()
            startCheckLogicThinking()
          },
          onCheckLogicThinkingDelta: (delta) => updateCheckLogicThinking(delta),
          onCheckLogicThinkingComplete: () => finishCheckLogicThinking(),
          // CheckLogic edits - apply to canvas in real-time
          onCheckLogicEdit: (oldStr, newStr) => {
            console.log('[DChat] CheckLogic edit:', oldStr.slice(0, 50), '→', newStr.slice(0, 50))
            applyCanvasEdit(oldStr, newStr)
          },
          // CheckLogic done
          onCheckLogicDone: (output) => {
            console.log('[DChat] CheckLogic done - full output:', output)
            finishCheckLogicProcessing(output)
          },
          // RhythmSound (micro editing - Pass 3)
          onRhythmSoundStart: () => {
            startRhythmSoundProcessing()
          },
          onRhythmSoundThinkingStart: () => {
            startRhythmSoundProcessing()
            startRhythmSoundThinking()
          },
          onRhythmSoundThinkingDelta: (delta) => updateRhythmSoundThinking(delta),
          onRhythmSoundThinkingComplete: () => finishRhythmSoundThinking(),
          // RhythmSound edits - apply to canvas in real-time
          onRhythmSoundEdit: (oldStr, newStr) => {
            console.log('[DChat] RhythmSound edit:', oldStr.slice(0, 50), '→', newStr.slice(0, 50))
            applyCanvasEdit(oldStr, newStr)
          },
          // RhythmSound done
          onRhythmSoundDone: (output) => {
            console.log('[DChat] RhythmSound done - full output:', output)
            finishRhythmSoundProcessing(output)
          },
          // Session complete - workflow finished, includes final diff
          onSessionComplete: (output) => {
            console.log('[DChat] Session complete:', output)
            if (output.final_diff) {
              setFinalDiff(output.final_diff as FinalDiff)
            }
          },
          // Responder (coaching response after editing)
          onResponderStart: () => {
            startResponderProcessing()
          },
          onResponderThinkingStart: () => {
            startResponderProcessing()
            startResponderThinking()
          },
          onResponderThinkingDelta: (delta) => updateResponderThinking(delta),
          onResponderThinkingComplete: () => finishResponderThinking(),
          onResponderDone: (output) => {
            console.log('[DChat] Responder done:', output)
            finishResponderProcessing(output)
          },
          // Legacy (backwards compat) - no-op since onGatekeeperDone handles it
          onDone: () => {},
          onError: (error) => setError(error),
        }
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }, [
    sessionId, documentContent, foolscapSnapshot, setWorkflowStatus, setError,
    addGatekeeperUserTurn, startGatekeeperStreaming, finishGatekeeperStreaming,
    startZinsserProcessing, startZinsserThinking, updateZinsserThinking,
    finishZinsserThinking, finishZinsserProcessing, applyCanvasEdit, applyCanvasInsert,
    startStrengthenVerbsProcessing, startStrengthenVerbsThinking,
    updateStrengthenVerbsThinking, finishStrengthenVerbsThinking, finishStrengthenVerbsProcessing,
    startCheckLogicProcessing, startCheckLogicThinking, updateCheckLogicThinking,
    finishCheckLogicThinking, finishCheckLogicProcessing, startRhythmSoundProcessing,
    startRhythmSoundThinking, updateRhythmSoundThinking, finishRhythmSoundThinking,
    finishRhythmSoundProcessing, setFinalDiff, startResponderProcessing,
    startResponderThinking, updateResponderThinking, finishResponderThinking,
    finishResponderProcessing,
  ])

  // Trigger workflow when pendingInitialTrigger is set (after session creation from picker)
  // This is a one-time trigger that's cleared immediately to prevent re-triggering on refresh
  useEffect(() => {
    if (pendingInitialTrigger && sessionId && workflowStatus === 'idle') {
      console.log('[DChat] Triggering initial draft for new session:', sessionId)
      clearPendingInitialTrigger()
      // Trigger with empty content - the hidden initial turn is already in gatekeeperTurns
      triggerDrafter('')
    }
  }, [pendingInitialTrigger, sessionId, workflowStatus, clearPendingInitialTrigger, triggerDrafter])

  // Auto-scroll to bottom when new messages arrive or content is streaming
  // Use instant scroll during streaming for smoother following, smooth scroll otherwise
  useEffect(() => {
    const isStreaming = workflowStatus === 'streaming'
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
      block: 'end'
    })
  }, [
    gatekeeperTurns,
    zinsserStreamingThinking,
    zinsserIsThinking,
    strengthenVerbsStreamingThinking,
    strengthenVerbsIsThinking,
    checkLogicStreamingThinking,
    checkLogicIsThinking,
    rhythmSoundStreamingThinking,
    rhythmSoundIsThinking,
    workflowStatus
  ])

  const handleSubmit = async (content: string) => {
    if (!content.trim() || workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming') {
      return
    }
    await triggerDrafter(content)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-0 md:pr-6 scroll-touch overscroll-contain">
        <div className="max-w-2xl py-4 md:py-6 mx-auto md:mx-0">
          {/* Filter out hidden turns for display */}
          {(() => {
            const visibleTurns = gatekeeperTurns.filter(turn => !turn.isHidden)
            const hasNoVisibleTurns = visibleTurns.length === 0 && workflowStatus !== 'submitting' && workflowStatus !== 'streaming'

            return hasNoVisibleTurns ? (
              <div className="text-center py-12">
                <h2 className="text-lg font-light text-foreground mb-2">
                  Ready to draft
                </h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Your Foolscap is loaded. Tell me how you'd like to approach this draft.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {visibleTurns.map((turn, index) => {
                  const isLastTurn = index === visibleTurns.length - 1
                  const isProcessing = workflowStatus === 'streaming' || workflowStatus === 'processing'
                  return (
                    <DChatMessage
                      key={index}
                      turn={turn}
                      turnIndex={index}
                      isCurrentTurn={isLastTurn}
                      isProcessing={isProcessing}
                    />
                  )
                })}
              {workflowStatus === 'submitting' && (
                <DThinkingIndicator />
              )}
              {(workflowStatus === 'streaming' || workflowStatus === 'processing') && (
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Zinsser (Drafter) - pulsing amber while active, green when complete */}
                    {zinsserIsProcessing && (
                      <div>
                        {zinsserStreamingThinking && (
                          <DThinkingBlock
                            thinking={zinsserStreamingThinking}
                            isStreaming={zinsserIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-amber-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                          <span>Drafter</span>
                        </div>
                      </div>
                    )}
                    {zinsserHasThinking && !zinsserIsProcessing && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Drafter</span>
                      </div>
                    )}

                    {/* StrengthenVerbs - pulsing violet while active, green when complete */}
                    {strengthenVerbsIsProcessing && (
                      <div className="mt-3">
                        {strengthenVerbsStreamingThinking && (
                          <DThinkingBlock
                            thinking={strengthenVerbsStreamingThinking}
                            isStreaming={strengthenVerbsIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-violet-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                          <span>Verbs</span>
                        </div>
                      </div>
                    )}
                    {strengthenVerbsHasThinking && !strengthenVerbsIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Verbs</span>
                      </div>
                    )}

                    {/* CheckLogic - pulsing cyan while active, green when complete */}
                    {checkLogicIsProcessing && (
                      <div className="mt-3">
                        {checkLogicStreamingThinking && (
                          <DThinkingBlock
                            thinking={checkLogicStreamingThinking}
                            isStreaming={checkLogicIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-cyan-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />
                          <span>Logic</span>
                        </div>
                      </div>
                    )}
                    {checkLogicHasThinking && !checkLogicIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Logic</span>
                      </div>
                    )}

                    {/* RhythmSound - pulsing indigo while active, green when complete */}
                    {rhythmSoundIsProcessing && (
                      <div className="mt-3">
                        {rhythmSoundStreamingThinking && (
                          <DThinkingBlock
                            thinking={rhythmSoundStreamingThinking}
                            isStreaming={rhythmSoundIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-indigo-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
                          <span>Rhythm</span>
                        </div>
                      </div>
                    )}
                    {rhythmSoundHasThinking && !rhythmSoundIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Rhythm</span>
                      </div>
                    )}

                    {/* Responder - pulsing pink while generating response */}
                    {responderIsProcessing && (
                      <div className="mt-3">
                        {responderStreamingThinking && (
                          <DThinkingBlock
                            thinking={responderStreamingThinking}
                            isStreaming={responderIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-pink-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-pink-500 rounded-full animate-pulse" />
                          <span>Responding</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            )
          })()}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 py-3 md:py-4 px-4 md:px-0 md:pr-6 border-t border-border md:border-0">
        <div className="max-w-2xl mx-auto md:mx-0">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <DChatInput
                onSubmit={handleSubmit}
                disabled={workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming'}
                placeholder={
                  gatekeeperTurns.length === 0
                    ? "How would you like to draft this?"
                    : "Continue the conversation..."
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
